import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material';
import { Keys, StorageService } from '../../service/storage.service';
import { LoanDetailsService } from '../../service/loan-details.service';
import { Loan } from '../../model/loan.model';
import { LoanRestructureService } from '../../service/loan-restructure.service';
import { WriteOffService } from '../../service/write-off.service';
import { ConfigService } from '../../service/config.service';
import { EmiStatus } from '../../model/emi-status';
import { PrepaymentStatus } from '../../model/prepayment-status';
import { WriteOffStatus, WriteOffTag } from '../../model/WriteOff';
import { LoanRestructure } from '../../model/loan-restructure';
import { EmiMoratorium } from '../../model/emi-moratorium.model';
import { EmiMoratoriumService } from '../../service/emi-moratorium.service';
import { LoanStatusType } from '../../model/loan-status.model';
import moment = require('moment');
import { animate, state, style, transition, trigger } from '@angular/animations';


interface LenderRestructuredLoan {
    loanId: string;
    rating: string;
    investmentInLoan: number;
    statusBeforeRestructure: string;
    restructuringDate: string;
    grossCashFlowReceivedBeforeRestructure: number;
    principalOutstandingPostRestructure: number;
    principalWriteOffOrWriteBack: number;
    restructuredFirstEmiDate: string;
    tenurePostRestructure: number;
    roiPostRestructure: number;
    loanSanctionStatus: string;
    upfrontPayment: number;

}

export enum RestructuredLoanStatus {
    Regular = 'Regular',
    Delayed = 'Delayed',
    Npa = 'NPA',
    SubStandAsset = 'Sub Standard Asset',
    LossAsset = 'Loss Asset'
}


@Component({
    selector: 'app-restructured-loans',
    templateUrl: './restructured-loans.component.html',
    styleUrls: ['./restructured-loans.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*', minHeight: "*" })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ]
})
export class RestructuredLoansComponent implements OnInit {
    displayedColumns = [
        'loanId',
        'investmentInLoan',
        'grossCashFlowReceivedBeforeRestructure',
        'statusBeforeRestructure',
        'restructuringDate',
        'upfrontPayment',
        'principalOutstandingPostRestructure',
        'principalWriteOffOrWriteBack',
        'restructuredFirstEmiDate',
        'loanSanctionStatus'
    ];
    dataSource = new MatTableDataSource();
    networkCallInProgress: boolean;

    private readonly storageService: StorageService;
    private readonly loanDetailService: LoanDetailsService;
    private readonly loanRestructureService: LoanRestructureService;
    private readonly writeOffService: WriteOffService;
    private readonly configService: ConfigService;
    private readonly emiMoratoriumService: EmiMoratoriumService;
    expandedElement: any | null;
    public isMobile: boolean = false;
    screenWidth: number;

    constructor(storageService: StorageService, loanDetailService: LoanDetailsService,
        loanRestructureService: LoanRestructureService, writeOffService: WriteOffService,
        configService: ConfigService, emiMoratoriumService: EmiMoratoriumService) {
        this.storageService = storageService;
        this.loanDetailService = loanDetailService;
        this.loanRestructureService = loanRestructureService;
        this.writeOffService = writeOffService;
        this.configService = configService;
        this.emiMoratoriumService = emiMoratoriumService;
    }
    ngAfterViewInit() {
        this.screenWidth = window.innerWidth;
        this.isMobile = (<any>window).innerWidth < 500;
        if (this.isMobile) {
            this.displayedColumns = ['loanId',
                'investmentInLoan',
                'grossCashFlowReceivedBeforeRestructure','accordion_arrow'];
        }
    }
    async ngOnInit(): Promise<void> {
        this.networkCallInProgress = true;
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        const restructuredLoans = await this.loanDetailService.getRestructuredLoansFor(lenderId);
        this.dataSource.data = await this.populateRestructureData(restructuredLoans, lenderId);
        this.networkCallInProgress = false;
    }

    // TODO: Duplicate Code
    public getDisplayableLoanStatus(loan: Loan) {
        if (loan.loan_sanction_status === LoanStatusType.DocumentHardcopyReceived) {
            const delayedDays = this.getNoOfDaysDelayed(loan);
            return delayedDays === 0 ? 'Loan disbursed and active' : this.getStatusBy(delayedDays);
        } else if (loan.loan_sanction_status === LoanStatusType.ClosedPrepayment) {
            return 'Prepaid and Closed';
        } else if (loan.loan_sanction_status === LoanStatusType.LoanMatured) {
            return 'Matured and Closed';
        } else if (loan.loan_sanction_status === LoanStatusType.SubStandardAsset) {
            return 'Sub Standard Asset';
        } else if (loan.loan_sanction_status === LoanStatusType.LossAsset) {
            return 'Loss Asset';
        } else if (loan.loan_sanction_status === LoanStatusType.ClosedSettlement) {
            return 'Settled & Closed';
        }
    }

    private async populateRestructureData(restructuredLoans: Loan[], lenderId: string): Promise<LenderRestructuredLoan[]> {
        const lenderRestructuredLoans: LenderRestructuredLoan[] = [];
        for (let i = 0; i < restructuredLoans.length; i++) {
            const loan = restructuredLoans[i];
            const investmentDetails = loan.lenders_invested.find(investment => investment.lender_id === lenderId);
            const lenderShare = investmentDetails.invested_amount / loan.loan_fulfilled_amount;
            const loanRestructure = await this.loanRestructureService.getLatestRestructuringByLoanId(loan._id);
            const delayedDaysBeforeRestructure = await this.getDelayedDaysBeforeRestructure(loan, loanRestructure.firstDelayedEmiDueOn, loanRestructure.restructuredOn);
            lenderRestructuredLoans.push(
                {
                    loanId: loan.generated_loan_id,
                    rating: loan.rating,
                    investmentInLoan: investmentDetails.invested_amount,
                    statusBeforeRestructure: this.getStatusBy(delayedDaysBeforeRestructure),
                    restructuringDate: moment(loanRestructure.restructuredOn).format('DD MMM YYYY'),
                    grossCashFlowReceivedBeforeRestructure: this.getGrossCashFlowReceivedBeforeRestructure(loan, loanRestructure.restructuredOn, loanRestructure) * lenderShare,
                    principalOutstandingPostRestructure: this.getOutstandingPrincipal(loan) * lenderShare,
                    principalWriteOffOrWriteBack: await this.getWrittenOffPrincipal(loan) * lenderShare,
                    restructuredFirstEmiDate: moment(loanRestructure.firstEmiDueDate).format('DD MMM YYYY'),
                    tenurePostRestructure: loanRestructure.tenureInMonths,
                    roiPostRestructure: loanRestructure.yearlyRoiInPercentage,
                    loanSanctionStatus: this.getDisplayableLoanStatus(loan),
                    upfrontPayment: loanRestructure.upfrontPaymentTotal * lenderShare
                });
        }
        return lenderRestructuredLoans;
    }

    private getStatusBy(noOfDelayedDays) {
        const npaDays = this.configService.getNpaDays();
        const substandardAssetThresholdDays = this.configService.getSubstandardAssetWriteOffThresholdInDays();
        const lossAssetThresholdDays = this.configService.getLossAssetWriteOffThresholdInDays();

        if (noOfDelayedDays === 0) {
            return RestructuredLoanStatus.Regular;
        } else if (noOfDelayedDays < npaDays) {
            return RestructuredLoanStatus.Delayed;
        } else if (noOfDelayedDays >= npaDays && noOfDelayedDays < substandardAssetThresholdDays) {
            return RestructuredLoanStatus.Npa;
        } else if (noOfDelayedDays >= substandardAssetThresholdDays && noOfDelayedDays < lossAssetThresholdDays) {
            return RestructuredLoanStatus.SubStandAsset;
        } else {
            return RestructuredLoanStatus.LossAsset;
        }
    }

    private getGrossCashFlowReceivedBeforeRestructure(loan: Loan, restructuredOn: Date, loanRestructure: LoanRestructure) {
        let grossCashFlowReceived = 0;
        loan.emi_schedule.filter(emi => emi.paid_on < restructuredOn).forEach(emi => {
            if ((emi.status === EmiStatus.Received || emi.status === EmiStatus.Completed)) {
                const penalInterest = emi.penalInterest ? emi.penalInterest : 0;
                grossCashFlowReceived += emi.emi_amount + penalInterest;
            }
        });
        if (loan.prepayment_details) {
            loan.prepayment_details.details.filter(payment => payment.payment_date < restructuredOn).forEach(payment => {
                if (payment.prepayment_status === PrepaymentStatus.PartpaymentCompleted || payment.prepayment_status === PrepaymentStatus.PrepaymentCompleted) {
                    grossCashFlowReceived += payment.prepayment_principal + payment.prepayment_interest;
                }
            });
        }

        return grossCashFlowReceived;
    }

    // TODO: Duplicate Code
    private getOutstandingPrincipal(loan: Loan) {
        if ([LoanStatusType.ClosedSettlement, LoanStatusType.LoanMatured, LoanStatusType.ClosedPrepayment].includes(loan.loan_sanction_status)) {
            return 0;
        }
        const delayedEmis = loan.emi_schedule.filter(emi => !['emi_received', 'emi_completed'].includes(emi.status))
            .filter(emi => new Date(emi.due_on));
        const firstDelayedEmi = delayedEmis.find(emi => !!emi);
        return firstDelayedEmi.balance_amount + firstDelayedEmi.principal_repayment;
    }

    private async getWrittenOffPrincipal(loan) {
        const writeOff = await this.writeOffService.getWriteOffByLoanId(loan._id);
        if (!writeOff) {
            return 0;
        }
        const restructureDetails = writeOff.history.find(history => history.tag === WriteOffTag.LoanRestructure);
        if (!restructureDetails) {
            return 0;
        }
        return restructureDetails.status === WriteOffStatus.WriteOff ? -restructureDetails.principal : restructureDetails.principal;
    }

    private async getEmiMoratorium(loanId: string): Promise<EmiMoratorium> {
        const emiMoratoriums = await this.emiMoratoriumService.getEmiMoratoriumsByLoanId(loanId);
        return emiMoratoriums && emiMoratoriums.find(emiMoratorium => emiMoratorium.deletedAt === undefined);
    }

    private async getDelayedDaysBeforeRestructure(loan: Loan, firstDelayedEmiDueOn: Date, restructuredOn: Date): Promise<number> {
        let noOfDelayedDays = 0, moratoriumDays = 0;
        if (!firstDelayedEmiDueOn) {
            return noOfDelayedDays;
        } else {
            const emiMoratorium = await this.getEmiMoratorium(loan._id);
            if (emiMoratorium && firstDelayedEmiDueOn < emiMoratorium.startsAt) {
                moratoriumDays = moment(emiMoratorium.startsAt).endOf('day')
                    .diff(moment(emiMoratorium.endsAt).endOf('month').toDate());
            }
            noOfDelayedDays = moment(restructuredOn).endOf('day').diff(moment(firstDelayedEmiDueOn).endOf('day'), 'days');
            return noOfDelayedDays - moratoriumDays;
        }

    }

    // TODO: Duplicate code
    private getNoOfDaysDelayed(loan: Loan) {
        let delayedDays = 0;
        const outStandingDelay = loan.emi_delay_details.emi_delay.find(delay => !delay.is_delay_amount_paid);
        if (!(outStandingDelay && outStandingDelay.due_date)) {
            return delayedDays;
        } else {
            delayedDays = moment().endOf('day').diff(moment(outStandingDelay.due_date).endOf('day'), 'days');
            const gracePeriodDays = this.configService.getEmiGracePeriodInDays();
            return delayedDays > gracePeriodDays ? delayedDays : 0;
        }
    }
}
